import { useMemo, useState } from 'react'
import CLUB from '../../config/club.js'
import { SAMPLE_TOURNAMENT } from './sampleTournament.js'
import {
  teamCupFieldFromSample, applyOffTheLowest, strokesForRound,
  computeTeamMatchResults, computeScatPayouts, computeCthValues,
} from './ggTeamCup.js'

// G-TeamCup — isolated sandbox recreation of the live Team Cup, fully
// self-contained (own engine in ggTeamCup.js, zero live imports). READ-ONLY,
// computed from the in-code SAMPLE_TOURNAMENT: its six 2-player teams are the
// competing teams. Gross/Net best ball · Official/Custom · Off-the-Lowest ·
// Scats · CTH. Print reuses the app's window.print() pattern.

// Deterministic CTH picks for the sample's four par 3s (holes 3/6/11/15).
// Hole 6 is "none" to demonstrate the carryover onto the remaining par 3s.
const SAMPLE_CTH = { 3: 't1_p1', 6: 'none', 11: 't3_p1', 15: 't5_p2' }

const fmtPts = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10))

const Seg = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
    {options.map((o) => {
      const on = value === o.value
      return (
        <button key={String(o.value)} onClick={() => onChange(o.value)}
          className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
          style={on ? { backgroundColor: '#1B4D2E', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>
          {o.label}
        </button>
      )
    })}
  </div>
)

export default function GTeamCup() {
  const [net, setNet] = useState(false)
  const [scoringSystem, setScoringSystem] = useState('official')
  const [offLow, setOffLow] = useState(false)
  const [scatsOn, setScatsOn] = useState(false)
  const [scatsAmt, setScatsAmt] = useState(5)
  const [cthOn, setCthOn] = useState(false)
  const [cthAmt, setCthAmt] = useState(20)

  const field = useMemo(() => teamCupFieldFromSample(SAMPLE_TOURNAMENT), [])
  const pars = SAMPLE_TOURNAMENT.info.pars
  const strokeIndex = SAMPLE_TOURNAMENT.info.strokeIndex

  const match = useMemo(() => {
    const ch = net && offLow ? applyOffTheLowest(field.courseHandicaps) : field.courseHandicaps
    const strokesByPlayer = strokesForRound(ch, strokeIndex)
    return computeTeamMatchResults({
      teams: field.teams, scoresByHole: field.scoresByHole,
      strokesByPlayer, handicapsOn: net, scoringSystem,
    })
  }, [field, net, offLow, scoringSystem, strokeIndex])

  const scats = useMemo(() => scatsOn
    ? computeScatPayouts({
        playerIds: field.players.map((p) => p.id),
        scoresByGroup: { field: field.scoresByHole },
        perHole: Number(scatsAmt) || 0, roundFinal: true,
      })
    : null, [scatsOn, scatsAmt, field])

  const cth = useMemo(() => cthOn
    ? computeCthValues({ pars, cthByHole: SAMPLE_CTH, groupCodes: ['field'], perPar3: Number(cthAmt) || 0 })
    : null, [cthOn, cthAmt, pars])

  const teamName = (id) => field.teams.find((t) => t.id === id)?.name || id
  const playerName = (pid) => field.players.find((p) => p.id === pid)?.name || pid
  const ranked = [...field.teams].sort((a, b) => (match.teamPoints[b.id] || 0) - (match.teamPoints[a.id] || 0))

  return (
    <div className="card">
      <style>{PRINT_CSS}</style>
      <div className="g-screen space-y-3 mb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Seg options={[{ value: false, label: 'Gross' }, { value: true, label: 'Net' }]} value={net} onChange={setNet} />
            <Seg options={[{ value: 'official', label: 'Official' }, { value: 'custom', label: 'Custom' }]} value={scoringSystem} onChange={setScoringSystem} />
            {net && (
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                <input type="checkbox" checked={offLow} onChange={(e) => setOffLow(e.target.checked)} />
                Off the Lowest
              </label>
            )}
          </div>
          <button className="btn-primary" onClick={() => window.print()}>🖨 Print</button>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
            <input type="checkbox" checked={scatsOn} onChange={(e) => setScatsOn(e.target.checked)} /> Scats
            {scatsOn && <span className="flex items-center gap-1 text-gray-500">$<input type="number" className="w-14 border rounded px-1 py-0.5" value={scatsAmt} onChange={(e) => setScatsAmt(e.target.value)} />/hole</span>}
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
            <input type="checkbox" checked={cthOn} onChange={(e) => setCthOn(e.target.checked)} /> Closest to Hole
            {cthOn && <span className="flex items-center gap-1 text-gray-500">$<input type="number" className="w-14 border rounded px-1 py-0.5" value={cthAmt} onChange={(e) => setCthAmt(e.target.value)} />/par 3</span>}
          </label>
        </div>
      </div>

      <div className="g-print">
        <div className="g-rpt-head">
          <img src={CLUB.logo} alt={CLUB.name} className="g-rpt-logo" />
          <div>
            <div className="g-rpt-club">{CLUB.name}</div>
            <div className="g-rpt-event">
              {SAMPLE_TOURNAMENT.info.name} · Team Cup · {scoringSystem === 'custom' ? 'Custom' : 'Official'}
              {net ? (offLow ? ' · Net (Off the Lowest)' : ' · Net') : ' · Gross'}
            </div>
          </div>
        </div>

        {/* Standings */}
        <h5 className="g-sub">Team Standings</h5>
        <table className="g-tbl">
          <thead><tr><th>Pos</th><th>Team</th><th>Points</th>{scoringSystem === 'custom' ? <th>Nine bonus (F/B)</th> : null}</tr></thead>
          <tbody>{ranked.map((t, i) => (
            <tr key={t.id}>
              <td>{i + 1}</td>
              <td>{t.name}</td>
              <td><b>{fmtPts(match.teamPoints[t.id] || 0)}</b></td>
              {scoringSystem === 'custom'
                ? <td className="g-muted">{fmtPts(match.ninePoints[t.id]?.front || 0)} / {fmtPts(match.ninePoints[t.id]?.back || 0)}</td>
                : null}
            </tr>
          ))}</tbody>
        </table>

        {/* Hole-by-hole */}
        <h5 className="g-sub" style={{ marginTop: 14 }}>Hole-by-hole{net ? ' · net best ball' : ' · gross best ball'}</h5>
        <table className="g-tbl">
          <thead><tr><th>Hole</th><th>Par</th><th>Winner</th></tr></thead>
          <tbody>{match.holes.map((h, i) => {
            let label = '—'
            if (h.winnerId === 'tie') label = 'Halved'
            else if (h.winnerId) label = teamName(h.winnerId)
            return (<tr key={i}><td>{i + 1}</td><td>{pars[i]}</td><td>{label}</td></tr>)
          })}</tbody>
        </table>

        {/* Scats */}
        {scats && (
          <>
            <h5 className="g-sub" style={{ marginTop: 14 }}>Scats · low gross field-wide · ${Number(scatsAmt) || 0}/hole</h5>
            <div className="g-two-col">
              <table className="g-tbl">
                <thead><tr><th>Player</th><th>Holes</th><th>$</th></tr></thead>
                <tbody>{field.players.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{scats.holesWonByPlayer[p.id] || 0}</td>
                    <td><b style={{ color: (scats.payoutsByPlayer[p.id] || 0) >= 0 ? '#1B7A3E' : '#B42318' }}>
                      {(scats.payoutsByPlayer[p.id] || 0) >= 0 ? '+' : '−'}${Math.abs(scats.payoutsByPlayer[p.id] || 0)}
                    </b></td>
                  </tr>
                ))}</tbody>
              </table>
              <table className="g-tbl">
                <thead><tr><th>Hole</th><th>Winner</th></tr></thead>
                <tbody>{scats.perHole.map((r) => (
                  <tr key={r.hole}><td>{r.hole}</td><td className="g-muted">{r.winnerId === 'tie' ? 'Tie — no winner' : r.winnerId ? playerName(r.winnerId) : '—'}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}

        {/* CTH */}
        {cth && (
          <>
            <h5 className="g-sub" style={{ marginTop: 14 }}>Closest to Hole · par 3s · ${Number(cthAmt) || 0}/par 3 base</h5>
            <table className="g-tbl">
              <thead><tr><th>Hole</th><th>Winner</th><th>Paid</th></tr></thead>
              <tbody>{cth.perHole.map((r) => (
                <tr key={r.hole}>
                  <td>{r.hole}</td>
                  <td>{r.winnerId === 'none' ? 'None (carried)' : r.winnerId ? playerName(r.winnerId) : '—'}</td>
                  <td>{r.paid > 0 ? `$${r.paid}` : (r.carryFromHere > 0 ? `→ $${r.carryFromHere}` : '—')}</td>
                </tr>
              ))}</tbody>
            </table>
            {cth.leftoverBucket > 0 ? <p className="g-muted" style={{ marginTop: 6 }}>${cth.leftoverBucket} unclaimed (no par 3s left to carry).</p> : null}
          </>
        )}
      </div>
    </div>
  )
}

const PRINT_CSS = `
  .g-rpt-head { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid ${CLUB.accentColor}; margin-bottom: 14px; }
  .g-rpt-logo { height: 52px; object-fit: contain; }
  .g-rpt-club { font-weight: 800; font-size: 18px; color: ${CLUB.primaryColor}; }
  .g-rpt-event { font-size: 12px; color: #555; }
  .g-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
  .g-sub { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin: 0 0 4px; }
  .g-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .g-tbl th, .g-tbl td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  .g-tbl th { background: #f6f6f2; color: #555; }
  .g-muted { color: #999; }
  @media print {
    body * { visibility: hidden !important; }
    .g-print, .g-print * { visibility: visible !important; }
    .g-print { position: absolute; left: 0; top: 0; width: 100%; }
    .g-screen { display: none !important; }
    @page { size: portrait; margin: 0.4in; }
  }
`
